import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { RiDeleteBin6Fill } from 'react-icons/ri';
import { RiLink } from 'react-icons/ri';
import { MdModeEdit } from 'react-icons/md';
import { LoadingOutlined } from '@ant-design/icons';
import { DatePicker, Spin } from 'antd';
import { Toaster, toast } from 'react-hot-toast';
import Pagination from 'components/pagination';
import { apiCall } from 'utils/apiClient';
import ReviewShareLinkModal from 'layouts/reviews/ReviewShareLinkModal';

const QUICK_FILTERS = [
    { key: "today", label: "Today" },
    { key: "this_week", label: "This Week" },
    { key: "last_week", label: "Last Week" },
    { key: "this_month", label: "This Month" },
    { key: "last_month", label: "Last Month" },
];

const STATUS_OPTIONS = [
    { value: "pending", label: "Pending" },
    { value: "confirmed", label: "Confirmed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "completed", label: "Completed" },
    { value: "in_progress", label: "In Progress" },
    { value: "failed_payment", label: "Failed Payment" },
];

const STATUS_BADGES = {
    pending: "bg-amber-100 text-amber-700",
    confirmed: "bg-blue-100 text-blue-700",
    cancelled: "bg-rose-100 text-rose-700",
    completed: "bg-emerald-100 text-emerald-700",
    in_progress: "bg-violet-100 text-violet-700",
    failed_payment: "bg-red-100 text-red-700",
};

const ListBookings = () => {
    const antIcon = <LoadingOutlined style={{ fontSize: 60 }} spin />;
    const deletstyle = { color: "red" };
    const editstyle = { color: "green" };

    const [bookingData, setBookingData] = useState([]);
    const [filteredBookings, setFilteredBookings] = useState([]);
    const [Loader, setLoader] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [totalPages, setTotalPage] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [dateRange, setDateRange] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [quickFilter, setQuickFilter] = useState('');
    const [selectedReviewBooking, setSelectedReviewBooking] = useState(null);

    const navigate = useNavigate();

    const handleStatusChange = (e) => {
        setSelectedStatus(e.target.value);
        setCurrentPage(1);
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }, 300);

        return () => clearTimeout(delayDebounce);
    }, [searchTerm]);

    const getAllBookings = async () => {
        try {
            // setLoader(true);

            const body = {
                page: currentPage,
                limit: recordsPerPage,
                status: selectedStatus || undefined,
                search: debouncedSearchTerm || undefined,
                startDate: dateRange[0]?.format("YYYY-MM-DD"),
                endDate: dateRange[1]?.format("YYYY-MM-DD"),
                filter: quickFilter || undefined,
            };

            const result = await apiCall({
                endpoint: "booking",
                method: "POST",
                data: body,
            });

            if (result.status === 200) {
                const bookings = Array.isArray(result.data)
                    ? result.data
                    : Array.isArray(result.data?.data)
                        ? result.data.data
                        : [];
                setBookingData(bookings);
                setFilteredBookings(bookings);
                setTotalRecords(result.pagination?.total_records || 0);
                setTotalPage(result.pagination?.total_pages || 0);
            } else {
                console.error('Failed to fetch bookings:', result.message);
            }
        } catch (error) {
            console.error('Error fetching Bookings:', error);
        } finally {
            setLoader(false);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        // if (value === '') {
        //     setFilteredBookings(bookingData);
        // } else {
        //     const filtered = bookingData.filter((booking) =>
        //         booking?.user_name?.toLowerCase().includes(value.toLowerCase())
        //     );
        //     setFilteredBookings(filtered);
        // }
    };

    const handleToggleStatus = async (id, status) => {
        try {
            const result = await apiCall({
                endpoint: `booking/change-status/${id}`,
                method: "PUT",
                data: { status },
            });
            if (result.status === 200) {
                const updateList = (prevData) =>
                    prevData.map((el) =>
                        el.id === id ? { ...el, status } : el
                    );
                setBookingData(updateList);
                setFilteredBookings(updateList);
            } else {
                console.error("Error updating status:", result.message);
            }
        } catch (error) {
            console.error("Request failed:", error);
        }
    };

    useEffect(() => {
        getAllBookings();
    }, [currentPage, recordsPerPage, selectedStatus, debouncedSearchTerm, dateRange, quickFilter]);

    const handlePageChange = (number) => {
        setCurrentPage(number);
    };

    const handleRecordsPerPageChange = (value) => {
        setCurrentPage(1);
        setRecordsPerPage(value);
    };

    const handleDateRangeChange = (dates) => {
        setDateRange(dates || []);
        setCurrentPage(1);
    };

    const formatDate = (date) => {
        if (!date) return "-";
        const parsed = new Date(date);
        return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString();
    };

    const parseAmount = (value) => {
        if (value === null || value === undefined) return null;
        if (typeof value === "number") {
            return Number.isFinite(value) ? value : null;
        }
        if (typeof value === "string") {
            const cleaned = value.replace(/[^0-9.-]/g, "");
            if (!cleaned || cleaned === "-" || cleaned === "." || cleaned === "-.") return null;
            const parsed = Number(cleaned);
            return Number.isFinite(parsed) ? parsed : null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const formatAmount = (amount) => {
        const parsed = parseAmount(amount);
        return parsed !== null ? parsed.toLocaleString("en-IN") : "-";
    };

    const getDiscountAmount = (booking) => {
        const keys = [
            "discount",
            "discount_amount",
            "discountAmount",
            "coupon_discount",
            "manual_discount",
            "total_discount",
        ];

        for (const key of keys) {
            const parsed = parseAmount(booking?.[key]);
            if (parsed !== null) return parsed;
        }

        return null;
    };

    const getStatusLabel = (status) => {
        if (!status) return "-";
        return status.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const isCompletedBooking = (booking) => String(booking?.status || "").toLowerCase() === "completed";

    const clearFilters = () => {
        setSearchTerm("");
        setDebouncedSearchTerm("");
        setDateRange([]);
        setSelectedStatus("");
        setQuickFilter("");
        setCurrentPage(1);
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <Toaster position="top-center" reverseOrder={false} />
            {Loader ? (
                <div className='flex justify-center items-center h-[75vh] w-full'>
                    <Spin indicator={antIcon} />
                </div>
            ) : (
                <div className="mt-6 px-4 sm:px-6">
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <div className="text-lg font-semibold text-gray-900">Bookings</div>
                                <div className="text-xs text-gray-500">
                                    Showing {filteredBookings.length} of {totalRecords}
                                </div>
                            </div>
                        </div>

                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-2">
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    className="h-9 w-full bg-white border border-gray-300 text-gray-900 text-sm rounded-md px-3 focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                                    placeholder="Search customer..."
                                />

                                <DatePicker.RangePicker
                                    onChange={handleDateRangeChange}
                                    format="YYYY-MM-DD"
                                    className="w-full h-9"
                                    value={dateRange}
                                />

                                <select
                                    value={selectedStatus}
                                    onChange={handleStatusChange}
                                    className="h-9 w-full text-sm bg-white border border-gray-300 rounded-md px-2 focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                                >
                                    <option value="">All Status</option>
                                    {STATUS_OPTIONS.map((status) => (
                                        <option key={status.value} value={status.value}>
                                            {status.label}
                                        </option>
                                    ))}
                                </select>

                                <div className="xl:col-span-2 flex flex-wrap items-center gap-2">
                                    {QUICK_FILTERS.map((filter) => (
                                        <button
                                            key={filter.key}
                                            onClick={() => {
                                                setQuickFilter(filter.key);
                                                setCurrentPage(1);
                                            }}
                                            className={`text-xs px-3 h-8 rounded-md transition-colors ${
                                                quickFilter === filter.key
                                                    ? "bg-blue-600 text-white"
                                                    : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                            }`}
                                        >
                                            {filter.label}
                                        </button>
                                    ))}
                                    <button
                                        onClick={clearFilters}
                                        className="text-xs px-3 h-8 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 ml-auto"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm text-left text-gray-700">
                                <thead className="text-[11px] text-gray-500 uppercase bg-gray-50 tracking-wide">
                                    <tr className="border-b border-gray-200">
                                        <th className="py-2 px-3 whitespace-nowrap">Sr No</th>
                                        <th className="py-2 px-3 whitespace-nowrap">Customer</th>
                                        <th className="py-2 px-3 whitespace-nowrap">Booking ID</th>
                                        <th className="py-2 px-3 whitespace-nowrap text-right">Total</th>
                                        <th className="py-2 px-3 whitespace-nowrap text-right">Discount</th>
                                        <th className="py-2 px-3 whitespace-nowrap text-right">Paid</th>
                                        <th className="py-2 px-3 whitespace-nowrap">Payment Type</th>
                                        <th className="py-2 px-3 whitespace-nowrap">Payment Status</th>
                                        <th className="py-2 px-3 whitespace-nowrap">Date</th>
                                        <th className="py-2 px-3 whitespace-nowrap">Status</th>
                                        <th className="py-2 px-3 whitespace-nowrap text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredBookings?.length > 0 ? (
                                        filteredBookings.map((el, index) => (
                                            <tr className="bg-white border-b border-gray-100 hover:bg-gray-50" key={el.id}>
                                                <td className="py-2 px-3 whitespace-nowrap">
                                                    {(currentPage - 1) * recordsPerPage + index + 1}
                                                </td>
                                                <td className="py-2 px-3 whitespace-nowrap">
                                                    <button
                                                        className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
                                                        onClick={() => navigate('/booking-details', { state: { booking_id: el.id } })}
                                                    >
                                                        {el.user_name || "-"}
                                                    </button>
                                                </td>
                                                <td className="py-2 px-3 whitespace-nowrap">{el.booking_code || "-"}</td>
                                                <td className="py-2 px-3 whitespace-nowrap text-right">{formatAmount(el.final_amount)}</td>
                                                <td className="py-2 px-3 whitespace-nowrap text-right">{formatAmount(getDiscountAmount(el))}</td>
                                                <td className="py-2 px-3 whitespace-nowrap text-right">{formatAmount(el.paid_amount)}</td>
                                                <td className="py-2 px-3 whitespace-nowrap">{el.payment_type || "-"}</td>
                                                <td className="py-2 px-3 whitespace-nowrap">{el.payment_status || "-"}</td>
                                                <td className="py-2 px-3 whitespace-nowrap">{formatDate(el.created_at)}</td>
                                                <td className="py-2 px-3 whitespace-nowrap">
                                                    <span
                                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                            STATUS_BADGES[el.status] || "bg-gray-100 text-gray-700"
                                                        }`}
                                                    >
                                                        {getStatusLabel(el.status)}
                                                    </span>
                                                </td>

                                                <td className="py-2 px-3 whitespace-nowrap">
                                                    <div className="flex items-center justify-end gap-2">
                                                        {el.status !== "completed" && el.status !== "cancelled" && (
                                                            <select
                                                                value={el.status}
                                                                onChange={(e) => handleToggleStatus(el.id, e.target.value)}
                                                                className="text-xs border border-gray-300 rounded-md px-2 py-1 h-8"
                                                            >
                                                                {STATUS_OPTIONS.map((status) => (
                                                                    <option key={status.value} value={status.value}>
                                                                        {status.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        )}

                                                        {el.status !== "completed" && el.status !== "cancelled" && (
                                                            <MdModeEdit
                                                                size={20}
                                                                style={editstyle}
                                                                onClick={() => navigate(`/bookings/edit/${el.id}`)}
                                                                className="cursor-pointer"
                                                                title="Edit Booking"
                                                            />
                                                        )}

                                                        {isCompletedBooking(el) && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setSelectedReviewBooking(el)}
                                                                className="inline-flex h-8 items-center gap-1 rounded-md bg-blue-600 px-2 text-xs font-semibold text-white hover:bg-blue-700"
                                                                title="Create review link"
                                                            >
                                                                <RiLink size={15} />
                                                                Review
                                                            </button>
                                                        )}

                                                        <RiDeleteBin6Fill
                                                            size={20}
                                                            style={deletstyle}
                                                            className="cursor-pointer"
                                                            title="Delete Booking"
                                                            onClick={async () => {
                                                                if (window.confirm("Are you sure you want to delete this booking?")) {
                                                                    try {
                                                                        const result = await apiCall({
                                                                            endpoint: `booking/delete/${el.id}`,
                                                                            method: "DELETE",
                                                                        });
                                                                        if (result.status === 200) {
                                                                            toast.success("Booking deleted");
                                                                            getAllBookings();
                                                                        } else {
                                                                            toast.error(result.message || "Failed to delete booking");
                                                                        }
                                                                    } catch (error) {
                                                                        toast.error("Error deleting booking");
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="11" className="py-6 text-center text-gray-500">No bookings available</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="px-2 py-2 border-t border-gray-200">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                                recordsPerPage={recordsPerPage}
                                onRecordsPerPageChange={handleRecordsPerPageChange}
                                totalRecords={totalRecords}
                                count={filteredBookings.length}
                            showFirstLastControls
                            compact
                            />
                        </div>
                    </div>
                </div>
            )}
            {selectedReviewBooking && (
                <ReviewShareLinkModal
                    open
                    booking={selectedReviewBooking}
                    onClose={() => setSelectedReviewBooking(null)}
                />
            )}
        </DashboardLayout>
    );
};

export default ListBookings;
